import React, { ReactElement, useContext, useEffect, useState } from 'react';
import './AccordionItem.scss';
import Chevron from '../Chevron/Chevron';
import { PlusMinus } from '../plus-minus/plus-minus';
import { BRAND } from '../../../constants';
import ServerContext from '../../../contexts/serverContext';
import { useAnalytics } from '../../../hooks/use-analytics';
import { AccordionClick } from '../../../services/shortcode-service/shortcode-service';

interface AccordionItemProps {
    children?: React.ReactNode;
    title?: string;
    titleHTML?: any;
    subtitle?: string;
    isFilledChevron?: boolean;
    chevronLabel?: string;
    accordionInfo?: ReactElement;
    id?: string;
    toggle?: boolean;
    eventName?: string;
    accordionIMG?: React.ReactNode;
    expanded?: boolean;
    disabled?: boolean;
    role?: string;
    tabIndex?: number;
    category?: string;
    subcategory?: string;
}

export default function AccordionItem(props: AccordionItemProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const serverContext = useContext(ServerContext);
    const handleClick = (id: string) => {
        setIsExpanded(!isExpanded);
        const models = document.getElementById(id) as HTMLUListElement;
        if (models) {
            if (models.style.display == 'none' || models.style.display == '') {
                models.style.display = 'grid';
            } else models.style.display = 'none';
        }
    };
    const [fireEvents] = useAnalytics();
    useEffect(() => {
        if (props.expanded && props.id) {
            if (!isExpanded) {
                handleClick(props.id);
            }
        }
    }, [props.expanded]);

    return (
        <div className={`accordion-item ${props.disabled ? 'disabled' : ''}`}>
            {props.title && (
                <button
                    aria-expanded={isExpanded}
                    className={`accordion-header ${isExpanded && 'expanded'} ${
                        props.disabled ? 'disabled' : ''
                    } `}
                    role={props?.role}
                    onClick={() => {
                        if (!props.disabled) {
                            handleClick(props?.id || props?.title || '');
                            if (props.eventName && !isExpanded) {
                                if (props.eventName === 'accordion-click') {
                                    const accordionClick: AccordionClick = {
                                        category: props.category,
                                        subcategory: props.subcategory,
                                        accordionName: props.title,
                                    };
                                    fireEvents(
                                        props.eventName,
                                        undefined,
                                        { accordionClick },
                                        false
                                    );
                                } else {
                                    const selfHelpFaq =
                                        props.title || 'no question';
                                    fireEvents(
                                        props.eventName,
                                        undefined,
                                        { selfHelpFaq },
                                        false
                                    );
                                }
                            }
                        }
                    }}
                >
                    <>
                        {props.accordionIMG && (
                            <div className="accordion-img">
                                {props.accordionIMG}
                            </div>
                        )}
                        <div className="accordion-titles">
                            {(props.titleHTML || props.title) && (
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: props.titleHTML || props.title,
                                    }}
                                />
                            )}
                            {props.subtitle && (
                                <span className="accordion-subtitle">
                                    {props.subtitle}
                                </span>
                            )}
                        </div>
                        {props.accordionInfo && (
                            <div className="accordion-info">
                                {props.accordionInfo}
                            </div>
                        )}
                        {serverContext.brand === BRAND.lincoln.LONG_NAME ? (
                            <div className="plus-minus-wrapper">
                                <PlusMinus isOpen={isExpanded} />
                            </div>
                        ) : (
                            <div className="accordion-chevron">
                                <span className="chevron-label">
                                    {props.chevronLabel}
                                </span>
                                <Chevron
                                    direction={isExpanded ? 'up' : 'down'}
                                    isFilled={props.isFilledChevron}
                                />
                            </div>
                        )}
                    </>
                </button>
            )}
            <div
                id={props.id || props.title}
                className={`accordion-description ${
                    props.toggle ? 'toggle' : ''
                }`.trim()}
                data-testid="accordion-description"
                tabIndex={props.tabIndex}
            >
                {props.children}
            </div>
        </div>
    );
}
